import { lazy, Suspense, use, useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom'
import { ContentBoundary, ContentLoading } from '@/components/ContentBoundary'
// Direct path, not the '@/features/globe' barrel — that barrel's
// `export * from './geo'` needs three.js's Vector3, and this file is the
// always-loaded App entry (not lazy), so importing anything from the
// barrel here would put three.js's ~560KB core into the one chunk every
// single route downloads regardless of whether it ever touches the globe.
import { BackToGlobeButton } from '@/features/globe/BackToGlobeButton'
import { GlobalLanguageSwitcher } from '@/features/i18n'
import { localizedPath, slugToLocale, useLanguageStore } from '@/services/i18n'
import { useProgressStore } from '@/services/progress'
import { hasDeviceId } from '@/services/identity/deviceIdStore'
import { ensureLandmarksLoaded } from '@/services/landmarks'
import { BillingSessionSync } from '@/features/access/BillingSessionSync'
import { PlaceAccessBoundary } from '@/features/access/PlaceAccessBoundary'
import { ModelAccessBoundary } from '@/features/access/ModelAccessBoundary'

// Route-level code splitting: each page becomes its own chunk instead of
// one 5MB+ bundle covering the globe (three/r3f/drei), every 3D model
// viewer, and the gallery all at once — see the PageSpeed Insights fix
// this addresses. Each page module has a named export, not a default one,
// so lazy() gets a small adapter rather than importing directly.
const HomePage = lazy(() => import('@/pages/HomePage').then((m) => ({ default: m.HomePage })))
const ExplorePage = lazy(() => import('@/pages/ExplorePage').then((m) => ({ default: m.ExplorePage })))
const PrintExplorePage = lazy(() => import('@/pages/PrintExplorePage').then((m) => ({ default: m.PrintExplorePage })))
const PrintPassportPage = lazy(() => import('@/pages/PrintPassportPage').then((m) => ({ default: m.PrintPassportPage })))
const PrintPostcardPage = lazy(() => import('@/pages/PrintPostcardPage').then((m) => ({ default: m.PrintPostcardPage })))
const LearningLandingPage = lazy(() => import('@/pages/LearningLandingPage').then((m) => ({ default: m.LearningLandingPage })))
const ModelGalleryPage = lazy(() =>
  import('@/pages/ModelGalleryPage').then((m) => ({ default: m.ModelGalleryPage })),
)
const ModelDetailPage = lazy(() =>
  import('@/pages/ModelDetailPage').then((m) => ({ default: m.ModelDetailPage })),
)
const AccountPage = lazy(() => import('@/pages/AccountPage').then((m) => ({ default: m.AccountPage })))
const PlansPage = lazy(() => import('@/pages/PlansPage').then((m) => ({ default: m.PlansPage })))

// FeedbackModal/FeedbackThankYouToast pull in @supabase/supabase-js (see
// supabaseClient.ts) — PageSpeed Insights' "reduce unused JavaScript"
// flagged ~122KB of Supabase SDK code shipped in the main chunk and parsed
// on every single page load, even though most visitors never open Feedback.
// FeedbackOverlays below gates these lazy components on the global store,
// so they remain reachable from FeedbackButton/FaqModal/PassportModal on
// every route without downloading the SDK until one is actually needed.
const FeedbackModal = lazy(() => import('@/features/feedback').then((m) => ({ default: m.FeedbackModal })))
const FeedbackThankYouToast = lazy(() =>
  import('@/features/feedback').then((m) => ({ default: m.FeedbackThankYouToast })),
)
const BadgeToast = lazy(() => import('@/features/badges/BadgeToast').then((m) => ({ default: m.BadgeToast })))

function BadgeToastHost() {
  const hasBadges = useProgressStore((s) => s.newlyEarnedBadges.length > 0)
  if (!hasBadges) return null
  return <Suspense fallback={null}><BadgeToast /></Suspense>
}

/**
 * Do not mount React.lazy components until their UI is actually needed:
 * merely placing a lazy component inside Suspense starts its import on the
 * first render. Both feedback views import the Supabase client, so mounting
 * them unconditionally made a fresh visitor download ~53KB of unused JS.
 */
function FeedbackOverlays() {
  const feedbackOpen = useProgressStore((s) => s.feedbackOpen)
  const hasPendingThankYous = useProgressStore((s) => s.pendingThankYous.length > 0)

  if (!feedbackOpen && !hasPendingThankYous) return null
  return (
    <Suspense fallback={null}>
      {feedbackOpen && <FeedbackModal />}
      {hasPendingThankYous && <FeedbackThankYouToast />}
    </Suspense>
  )
}

/**
 * Suspense fallback for route-level lazy-loading. Deliberately minimal —
 * this only shows for the brief window while a route chunk downloads
 * (usually imperceptible on a fast connection, a beat longer on throttled
 * mobile), not a full loading-screen experience. No spinner/branding, and
 * no background color of its own — body's own #04050f (index.css) already
 * shows through, matching the intro's "just space" first-launch feel
 * instead of introducing a different flash color.
 */
function RouteFallback() {
  return <div className="h-full w-full" />
}

/** Gate children themselves: a suspending sibling still lets routes render
 * once against an empty dataset and memoize empty search/game results. */
function LandmarksDataGate({ children }: { children: React.ReactNode }) {
  use(ensureLandmarksLoaded())
  return children
}

/**
 * index.html hardcodes `<html lang="en">` for the very first paint (before
 * any store has hydrated) — this keeps it in sync afterward. Screen readers
 * pick a pronunciation/voice from `lang`, so leaving it stuck on "en" after
 * a kid picks 简体中文/繁體中文 in LanguageSwitcher would have them read the
 * whole page with English phonetics. 'zh-Hans'/'zh-Hant' are already valid
 * BCP 47 tags, so no locale->lang mapping is needed here.
 */
function DocumentLangSync() {
  const locale = useLanguageStore((s) => s.locale)
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])
  return null
}

/**
 * Wraps the /:localeSlug/* route subtree (see <Routes> below) — validates
 * the slug against src/services/i18n/localeRoutes.ts's known locales and
 * syncs useLanguageStore to match it, so landing directly on e.g.
 * /zh-hans/explore/eiffel-tower (a search-engine visitor, a shared link,
 * a reload) shows Chinese immediately rather than whatever locale happened
 * to be persisted from a previous visit. An unrecognized slug (a stray bot
 * request, a typo) redirects home rather than rendering anything — this app
 * has no dedicated 404 page today, and a home redirect is a safe universal
 * fallback for an SPA.
 */
function LocaleRoute() {
  const { localeSlug } = useParams<{ localeSlug: string }>()
  const setLocale = useLanguageStore((s) => s.setLocale)
  const locale = localeSlug ? slugToLocale(localeSlug) : undefined

  useEffect(() => {
    if (locale) setLocale(locale)
  }, [locale, setLocale])

  if (!locale) return <Navigate to="/" replace />
  return <Outlet />
}

/** Teacher/classroom tools are retained in source for review, but are not
 * currently part of the public product. Keep the educational positioning;
 * direct utility links return visitors to the localized globe. */
function TeachingFeatureRedirect() {
  const locale = useLanguageStore((state) => state.locale)
  return <Navigate to={localizedPath(locale, '/')} replace />
}

/**
 * One-shot check, on app load, for any thank-you notes waiting for this
 * device (see feedbackClient.ts's checkForThankYou() and
 * FeedbackThankYouToast.tsx). Skips the network call entirely for a user
 * who's never opened the Feedback modal (hasDeviceId() false) — nothing to
 * check yet. setPendingThankYous() itself filters out anything already in
 * seenThankYouIds, so a returning user with everything already seen just
 * gets a cheap no-op fetch rather than needing a second guard here.
 */
function ThankYouCheck() {
  const setPendingThankYous = useProgressStore((s) => s.setPendingThankYous)
  useEffect(() => {
    // Dynamic import, not a static one — feedbackClient.ts pulls in
    // @supabase/supabase-js (see supabaseClient.ts), and hasDeviceId()
    // being false (never opened Feedback) is the common case for a fresh
    // visitor, so there's no reason to ship/parse that SDK in the main
    // chunk before even checking whether this call will do anything.
    if (!hasDeviceId()) return
    import('@/services/feedback/feedbackClient').then(({ checkForThankYou, getCurrentIdentity }) =>
      checkForThankYou(getCurrentIdentity()).then(setPendingThankYous),
    )
  }, [setPendingThankYous])
  return null
}

const PassportModal = lazy(() => import('@/features/badges/PassportModal').then(m => ({ default: m.PassportModal })))
const QuizModal = lazy(() => import('@/features/quiz/QuizModal').then(m => ({ default: m.QuizModal })))
const FaqModal = lazy(() => import('@/features/faq/FaqModal').then(m => ({ default: m.FaqModal })))

// Keep a dialog mounted after its first use so its own exit animation and
// cleanup still run. A closed dialog must not start a lazy import on boot.
function OptionalDialogs() {
  const passportOpen = useProgressStore(s => s.passportOpen)
  const quizOpen = useProgressStore(s => s.quizOpen)
  const faqOpen = useProgressStore(s => s.faqOpen)
  const [loaded, setLoaded] = useState({ passport: false, quiz: false, faq: false })
  useEffect(() => {
    setLoaded(previous => ({ passport: previous.passport || passportOpen, quiz: previous.quiz || quizOpen, faq: previous.faq || faqOpen }))
  }, [passportOpen, quizOpen, faqOpen])
  return <ContentBoundary><Suspense fallback={<ContentLoading />}>
    {(passportOpen || loaded.passport) && <PassportModal />}
    {(quizOpen || loaded.quiz) && <QuizModal />}
    {(faqOpen || loaded.faq) && <FaqModal />}
  </Suspense></ContentBoundary>
}

function App() {
  return (
    <>
        <BrowserRouter>
          <div className="h-full w-full">
            <DocumentLangSync />
            <ThankYouCheck />
            <BillingSessionSync />
            <ContentBoundary><Suspense fallback={<RouteFallback />}>
              <LandmarksDataGate>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/explore/:id" element={<PlaceAccessBoundary><ExplorePage /></PlaceAccessBoundary>} />
                <Route path="/print/explore/:id" element={<PlaceAccessBoundary><PrintExplorePage /></PlaceAccessBoundary>} />
                <Route path="/print/passport" element={<PrintPassportPage />} />
                <Route path="/print/postcard/:id" element={<PlaceAccessBoundary><PrintPostcardPage /></PlaceAccessBoundary>} />
                <Route path="/print/activity/:id" element={<TeachingFeatureRedirect />} />
                <Route path="/teachers/*" element={<TeachingFeatureRedirect />} />
                <Route path="/present/:id" element={<TeachingFeatureRedirect />} />
                <Route path="/classroom/*" element={<TeachingFeatureRedirect />} />
                <Route path="/learn/:slug" element={<LearningLandingPage />} />
                <Route path="/models" element={<ModelGalleryPage />} />
                <Route path="/models/:id" element={<ModelAccessBoundary><ModelDetailPage /></ModelAccessBoundary>} />
                <Route path="/plans" element={<PlansPage />} />
                <Route path="/account" element={<AccountPage />} />

                {/* Locale-prefixed equivalents (/zh-hans, /zh-hant — see
                    src/services/i18n/localeRoutes.ts) reuse the exact same
                    page components rather than duplicating each route, so
                    there's exactly one place each page's logic lives. English
                    deliberately has no prefix — see localeRoutes.ts. */}
                <Route path="/:localeSlug" element={<LocaleRoute />}>
                  <Route index element={<HomePage />} />
                  <Route path="explore/:id" element={<PlaceAccessBoundary><ExplorePage /></PlaceAccessBoundary>} />
                  <Route path="print/explore/:id" element={<PlaceAccessBoundary><PrintExplorePage /></PlaceAccessBoundary>} />
                  <Route path="print/passport" element={<PrintPassportPage />} />
                  <Route path="print/postcard/:id" element={<PlaceAccessBoundary><PrintPostcardPage /></PlaceAccessBoundary>} />
                  <Route path="print/activity/:id" element={<TeachingFeatureRedirect />} />
                  <Route path="teachers/*" element={<TeachingFeatureRedirect />} />
                  <Route path="present/:id" element={<TeachingFeatureRedirect />} />
                  <Route path="classroom/*" element={<TeachingFeatureRedirect />} />
                  <Route path="learn/:slug" element={<LearningLandingPage />} />
                  <Route path="models" element={<ModelGalleryPage />} />
                  <Route path="models/:id" element={<ModelAccessBoundary><ModelDetailPage /></ModelAccessBoundary>} />
                  <Route path="plans" element={<PlansPage />} />
                  <Route path="account" element={<AccountPage />} />
                </Route>
              </Routes>
              </LandmarksDataGate>
            </Suspense></ContentBoundary>
            {/* Mounted once here (not per-page) so an achievement earned from
                either HomePage's discovery card or ExplorePage's arrival
                always has somewhere to actually display — see BadgeToast.tsx.
                PassportModal is here for the same reason: "tap to view
                passport" from a toast needs to work regardless of current
                route, not just when HomePage's HUD happens to be mounted.
                BackToGlobeButton is here so every current/future non-home
                route gets a way back without remembering to add one.
                QuizModal is here so it can be launched from anywhere (HUD,
                Passport) rather than being tied to one page. FaqModal, same
                reasoning — HomePage's "?" button and the Passport's footer
                link both trigger it. GlobalLanguageSwitcher makes language
                switching work on every route the same way — it no-ops itself
                on '/' and '/explore/:id', which already embed their own
                switcher inline (see LanguageSwitcher.tsx). FeedbackModal/
                FeedbackThankYouToast are here for the same reasoning too —
                the modal itself needs to
                open from FaqModal/PassportModal's "send feedback" links
                regardless of route, even though its own trigger button
                (FeedbackButton) lives inline in HomePage's HUD row, not
                globally, so it reads as one of that row's actions rather
                than a separate floating element. */}
            <BadgeToastHost />
            <OptionalDialogs />
            <BackToGlobeButton />
            <FeedbackOverlays />
            <GlobalLanguageSwitcher />
          </div>
        </BrowserRouter>
    </>
  )
}

export default App
